// ResetPassword.jsx
import React, { useState } from "react";
import { Form } from "react-bootstrap";
import { Link, useNavigate, useParams } from "react-router-dom";
import AuthLayout from "./authLayout";
import { motion } from "framer-motion";
import { Eye, EyeOff } from "lucide-react";
import apiClient from "../api/config";
import { ROUTES } from "../routes";

export default function ResetPassword() {
  const { uidb64, token } = useParams();
  const navigate = useNavigate();

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError("");

    if (!password || !confirmPassword) {
      setError("Please fill in both password fields.");
      return;
    }
    if (password !== confirmPassword) {
      setError("Passwords do not match.");
      return;
    }

    setLoading(true);
    try {
      await apiClient.post("/api/reset-password/", { uidb64, token, new_password: password });
      setSuccess(true);
      setTimeout(() => navigate(ROUTES.LOGIN), 2500);
    } catch (err) {
      setError(err.response?.data?.error || "Unable to reset password. The link may have expired.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title="Reset password" subtitle="Choose a new password for your account">
      {success ? (
        <div className="text-center">
          <p style={{ color: "var(--color-text)", fontSize: 14, marginBottom: 12 }}>
            Your password has been updated. Redirecting you to sign in...
          </p>
          <Link to={ROUTES.LOGIN} className="auth-link" style={{ fontSize: 14 }}>
            Back to sign in
          </Link>
        </div>
      ) : (
        <Form onSubmit={handleSubmit}>
          {error && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-3"
              style={{
                background: "rgba(239,68,68,0.12)",
                color: "#ef4444",
                padding: "10px 14px",
                borderRadius: 10,
                fontSize: 14,
                fontWeight: 500,
              }}
            >
              {error}
            </motion.div>
          )}

          <Form.Group className="mb-3">
            <Form.Label style={{ color: "var(--color-text-light)" }}>New password</Form.Label>
            <div style={{ position: "relative" }}>
              <Form.Control
                type={showPassword ? "text" : "password"}
                value={password}
                placeholder="Enter new password"
                onChange={(e) => setPassword(e.target.value)}
                className="auth-input"
                style={{ background: "var(--color-bg)", color: "var(--color-text)", paddingRight: 40 }}
              />
              <button
                type="button"
                onClick={() => setShowPassword((prev) => !prev)}
                aria-label={showPassword ? "Hide password" : "Show password"}
                style={{
                  position: "absolute",
                  right: 12,
                  top: "50%",
                  transform: "translateY(-50%)",
                  background: "none",
                  border: "none",
                  padding: 0,
                  display: "flex",
                  alignItems: "center",
                  color: "var(--color-text-light)",
                  cursor: "pointer",
                }}
              >
                {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label style={{ color: "var(--color-text-light)" }}>Confirm new password</Form.Label>
            <Form.Control
              type={showPassword ? "text" : "password"}
              value={confirmPassword}
              placeholder="Re-enter new password"
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="auth-input"
              style={{ background: "var(--color-bg)", color: "var(--color-text)" }}
            />
          </Form.Group>

          <motion.button
            whileHover={{ scale: !loading ? 1.02 : 1 }}
            whileTap={{ scale: !loading ? 0.98 : 1 }}
            type="submit"
            className="w-100"
            disabled={loading}
            style={{
              background: "#6f42c1",
              border: "none",
              borderRadius: 12,
              padding: "10px 0",
              color: "#fff",
              fontWeight: 600,
              fontSize: 15,
              opacity: loading ? 0.8 : 1,
              transition: "0.2s",
            }}
          >
            {loading ? "Updating..." : "Reset password"}
          </motion.button>
        </Form>
      )}
    </AuthLayout>
  );
}
